import { Modal, Typography, Tooltip, Button, Grid } from "antd";
import styled from "styled-components";
import { useEffect, useState } from "react";
import useFirstVisit from "../../../hooks/useFirstVisit";
import {
  MailOutlined,
  FacebookOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";

type Props = {
  open: boolean;
  onClose: () => void;
};

const InfoModal = styled(Modal)`
  .ant-modal-content {
    background: #222831;
    color: #eeeeee;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  }
  .ant-modal-body {
    max-height: 70vh;
    overflow-y: auto;
  }
  @media (max-width: 768px) {
    .ant-modal-content {
      width: 92vw !important;
    }
  }
  .ant-modal-header {
    background: #222831;
    border-bottom: 1px solid #8e1616;
    border-radius: 12px 12px 0 0;
    padding: 12px 16px;
  }
  .ant-modal-title {
    color: #8e1616;
    font-weight: 700;
    letter-spacing: 0.5px;
  }
  .ant-modal-body {
    background: #222831;
    color: #eeeeee;
    padding: 16px;
  }
  .ant-modal-close {
    color: #eeeeee;
  }
  .ant-modal-close:hover {
    color: #8e1616;
    background: #393e46;
    border-radius: 8px;
  }
`;

const Paragraph = styled(Typography.Paragraph)`
  color: #eeeeee;
`;

const AccentText = styled(Typography.Text)`
  color: #8e1616;
  font-weight: 700;
`;

const ContactRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;
`;

const ContactLink = styled.a`
  color: #eeeeee;
  font-size: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 8px;
  transition:
    color 120ms ease,
    background 120ms ease;
  &:hover {
    color: #8e1616;
    background: #393e46;
  }
`;

const ContinueButton = styled(Button)`
  background: #8e1616;
  border-color: #8e1616;
  color: #eeeeee;
  border-radius: 8px;
  padding: 0 16px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover,
  &:focus {
    background: #393e46 !important;
    color: #eeeeee !important;
    border-color: #8e1616 !important;
  }
`;

export default function InformationModal({ open, onClose }: Props) {
  const screens = Grid.useBreakpoint();
  const { seen, markSeen } = useFirstVisit();
  const [visible, setVisible] = useState<boolean>(() => (seen ? open : true));

  useEffect(() => {
    if (seen) setVisible(open);
  }, [open, seen]);

  function handleClose() {
    markSeen();
    setVisible(false);
    onClose();
  }

  return (
    <InfoModal
      open={visible}
      title="Information about this site"
      width={screens.md ? 680 : "92%"}
      onCancel={handleClose}
      footer={
        !seen ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <ContinueButton onClick={handleClose}>Continue</ContinueButton>
          </div>
        ) : null
      }
      centered
    >
      <Paragraph>
        <AccentText>Crime Stalkers</AccentText> is a visual exploration tool for
        online investigators, researchers, and true-crime enthusiasts. It
        displays known victims of serial killers, missing persons, and unsolved
        cases on an interactive map — each marker representing a single
        individual or incident.
      </Paragraph>
      <Paragraph>
        The majority of the data currently available on Crime Stalkers focuses
        on cases from the <AccentText>1960s to early 1980s</AccentText>,
        primarily within the <AccentText>United States</AccentText>. This period
        is often associated with a high number of documented serial crime
        investigations. Over time, the goal is to expand the database to include
        additional decades, regions, and cases from around the world.
      </Paragraph>
      <Paragraph>
        Please note: all data shown is based on{" "}
        <AccentText>publicly available information</AccentText> and may contain{" "}
        <AccentText>inaccuracies or approximations</AccentText>, including
        location details. Crime Stalkers is intended as an informational and
        educational resource, not an official or authoritative database.
      </Paragraph>
      <Paragraph>
        If you have suggestions, ideas for improving the site, or would like to
        help contribute to building a{" "}
        <AccentText>more accurate and complete database</AccentText>, we would
        love to hear from you. Feel free to contact us through{" "}
        <AccentText>Facebook</AccentText> or by <AccentText>email</AccentText>.
        Community contributions help make this project better and more reliable
        for everyone.
      </Paragraph>
      <Paragraph>
        If you would like to support the continued development of Crime
        Stalkers, you can also support us on <AccentText>Patreon</AccentText> or
        similar platforms. Your support helps maintain the project, improve the
        database, and expand the research behind the map.
      </Paragraph>
      <ContactRow>
        <Tooltip title="Email">
          <ContactLink
            href="mailto:crimestalkersinfo@gmail.com"
            aria-label="Email"
          >
            <MailOutlined />
          </ContactLink>
        </Tooltip>
        <Tooltip title="Facebook">
          <ContactLink
            href="https://www.facebook.com/people/Crime-Stalkers/61583025975849/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <FacebookOutlined />
          </ContactLink>
        </Tooltip>
        <Tooltip title="YouTube">
          <ContactLink
            href="https://www.youtube.com/@CrimeStalkers-s5l"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
          >
            <YoutubeOutlined />
          </ContactLink>
        </Tooltip>
        <Tooltip title="Patreon">
          <ContactLink
            href="https://patreon.com/CrimeStalkers?utm_medium=unknown&utm_source=join_link&utm_campaign=creatorshare_creator&utm_content=copyLink"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Patreon"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M22.957 7.21c-.004-3.064-2.391-5.576-5.191-6.482c-3.478-1.125-8.064-.962-11.384.604C2.357 3.231 1.093 7.391 1.046 11.54c-.039 3.411.302 12.396 5.369 12.46c3.765.047 4.326-4.804 6.068-7.141c1.24-1.662 2.836-2.132 4.801-2.618c3.376-.836 5.678-3.501 5.673-7.031" />
            </svg>
          </ContactLink>
        </Tooltip>
      </ContactRow>
    </InfoModal>
  );
}
